// Error boundary da aplicação.
//
// Duas saídas, como combinado: "tentar novamente" (recarrega a tela) e "ir para
// a minha home" (a home do perfil). Erros que são só um deploy novo NÃO caem
// aqui — viram a tela de nova versão.

import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle, Home, RotateCw } from 'lucide-react'
import { HOME_BY_ROLE, type UserRole } from '@c1hd/shared'
import { isDeployError, notifyDeployDetected } from '../lib/deployDetector'
import { NewVersionScreen } from './NewVersionScreen'
import styles from './ErrorBoundary.module.css'

interface Props {
  children: ReactNode
  /** Perfil do usuário logado, para saber qual é a home dele. */
  role?: UserRole
}

interface State {
  error: Error | null
  isDeploy: boolean
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, isDeploy: false }

  static getDerivedStateFromError(error: Error): State {
    return { error, isDeploy: isDeployError(error) }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    if (isDeployError(error)) {
      notifyDeployDetected()
      return
    }
    console.error('[errorBoundary]', error, info.componentStack)
  }

  private readonly retry = () => {
    window.location.reload()
  }

  private readonly goHome = () => {
    const role = this.props.role
    window.location.assign(role ? HOME_BY_ROLE[role] : '/')
  }

  override render() {
    const { error, isDeploy } = this.state
    if (!error) return this.props.children
    if (isDeploy) return <NewVersionScreen />

    return (
      <div className={styles.wrap}>
        <div className={styles.card} role="alert">
          <span className={styles.badge}>
            <AlertTriangle size={26} aria-hidden="true" />
          </span>
          <h1 className={styles.title}>Alguma coisa saiu do lugar</h1>
          <p className={styles.text}>
            A tela não conseguiu continuar. Tente de novo, que na maioria das vezes
            resolve, ou volte para a sua página inicial. Nada do que você já tinha enviado
            foi perdido.
          </p>
          <div className={styles.actions}>
            <button type="button" className={styles.primary} onClick={this.retry}>
              <RotateCw size={18} aria-hidden="true" />
              Tentar novamente
            </button>
            <button type="button" className={styles.secondary} onClick={this.goHome}>
              <Home size={18} aria-hidden="true" />
              Ir para a minha home
            </button>
          </div>
          {import.meta.env.DEV && (
            <details className={styles.details}>
              <summary>Detalhes técnicos</summary>
              <pre>{error.stack ?? error.message}</pre>
            </details>
          )}
        </div>
      </div>
    )
  }
}
